import { getAuth0Client } from "@/auth0";
import type { Auth0UserResponse } from "@/plugins/launchDarkly/utils";
import { clearStoredAuth0Subject } from "@/utils/authIdentityConsistency";
import { clearLegacyAuthState, rebuildLegacyAuthStateFromAuth0 } from "@/utils/authState";
import { getAuthSessionManager } from "@/utils/authSessionManager";
import { notifyError } from "@/utils/notifier.js";

const AUTH_BOOTSTRAP_FAILURE_MESSAGE = "We could not restore your login session. Please sign in again.";

function isAuth0Callback(): boolean {
  const searchParams = new URLSearchParams(window.location.search);

  return searchParams.has("state") && (searchParams.has("code") || searchParams.has("error"));
}

function clearUnauthenticatedAuthState(): void {
  clearStoredAuth0Subject();
  clearLegacyAuthState();
  getAuthSessionManager().clearAuthenticatedSessionTracking();
}

export async function bootstrapAuthState(): Promise<void> {
  if (isAuth0Callback()) {
    return;
  }

  const auth0 = getAuth0Client();
  const authSessionManager = getAuthSessionManager();

  try {
    if (await authSessionManager.applySharedLogoutMarkerIfNeeded()) {
      return;
    }

    const isAuthenticated = await auth0.isAuthenticated();

    if (!isAuthenticated) {
      clearUnauthenticatedAuthState();
      return;
    }

    const auth0User = await auth0.getUser<Auth0UserResponse>();

    if (!auth0User) {
      clearUnauthenticatedAuthState();
      return;
    }

    const accessToken = await auth0.getTokenSilently();
    if (!rebuildLegacyAuthStateFromAuth0(auth0User, accessToken)) {
      clearUnauthenticatedAuthState();
      return;
    }

    authSessionManager.startAuthenticatedSessionTracking();
  } catch {
    clearUnauthenticatedAuthState();
    notifyError(false, AUTH_BOOTSTRAP_FAILURE_MESSAGE, "negative", "report_problem");
  }
}
